"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    sourcePrefix: string;
    sourceSuffix: string;
    ariaTitle: string;
    ariaDesc: string;
    marketRisk: string;
    riskAxis: string;
    holdingsAxis: string;
    chipEarly: string;
    chipLater: string;
    oneHolding: string;
    aroundTen: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    sourcePrefix: "参考: ",
    sourceSuffix: "を踏まえ筆者作成。",
    ariaTitle: "銘柄数とリスクの関係",
    ariaDesc:
      "保有銘柄数が増えるほど個別要因のリスクは大きく下がるが、その後は市場リスクの水準に近づき追加効果は緩やかになることを示す概念図。",
    marketRisk: "市場リスク（分散しても残る）",
    riskAxis: "リスク",
    holdingsAxis: "保有銘柄数",
    chipEarly: "最初は大きく低下",
    chipLater: "その後は逓減",
    oneHolding: "1銘柄",
    aroundTen: "10銘柄前後",
    title: "銘柄数とリスクの関係",
    description:
      "銘柄数を増やすと個別要因のリスクは下がりますが、市場全体に共通するリスクは残ります。効果は最初に大きく、その後は緩やかになります。",
  },
  en: {
    sourcePrefix: "Source: ",
    sourceSuffix: ", adapted by the author.",
    ariaTitle: "How risk relates to the number of holdings",
    ariaDesc:
      "A conceptual chart showing that risk from company-specific factors falls sharply as the number of holdings rises, then approaches the level of market risk, with the additional benefit tapering off.",
    marketRisk: "Market risk (remains however much you diversify)",
    riskAxis: "Risk",
    holdingsAxis: "Number of holdings",
    chipEarly: "A sharp fall at first",
    chipLater: "Tapering off after that",
    oneHolding: "1 holding",
    aroundTen: "Around 10 holdings",
    title: "How risk relates to the number of holdings",
    description:
      "Adding holdings lowers risk from company-specific factors, but risk common to the whole market remains. The benefit is large at first, then flattens.",
  },
};

// 分散投資の概念図
// - 最初は個別要因リスクが大きく下がる
// - その後は市場リスクに近づき、追加効果は逓減する
// 実務上の最適保有数を断定する図ではなく、教育用の概念図として扱う

const MARKET_RISK = 18;

const DATA = [
  { n: 1, risk: 48 },
  { n: 2, risk: 38 },
  { n: 3, risk: 33 },
  { n: 5, risk: 28 },
  { n: 7, risk: 25.5 },
  { n: 10, risk: 23 },
  { n: 15, risk: 21.2 },
  { n: 20, risk: 20.1 },
  { n: 30, risk: 19.1 },
  { n: 40, risk: 18.6 },
  { n: 50, risk: 18.3 },
];

const VW = 520;
const VH = 320;
const PAD = { top: 32, right: 30, bottom: 48, left: 56 };
const CW = VW - PAD.left - PAD.right;
const CH = VH - PAD.top - PAD.bottom;

const X_MAX = 50;
const Y_MIN = 16;
const Y_MAX = 52;

function toX(n: number) {
  return PAD.left + (n / X_MAX) * CW;
}

function toY(risk: number) {
  return PAD.top + ((Y_MAX - risk) / (Y_MAX - Y_MIN)) * CH;
}

function buildCurvePath(points: { x: number; y: number }[]) {
  return points
    .map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cpx = (prev.x + p.x) / 2;
      return `C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
    })
    .join(" ");
}

/** Chip label with a background that works in both light and dark mode */
function Chip({ x, y, text, fill }: { x: number; y: number; text: string; fill: string }) {
  const width = Math.max(42, text.length * 6.4 + 12);
  const height = 20;

  return (
    <g transform={`translate(${x - width / 2}, ${y - height / 2})`}>
      {/* Use currentColor-based background so it adapts to theme */}
      <rect width={width} height={height} rx={10} className="fill-white dark:fill-zinc-900" />
      <rect width={width} height={height} rx={10} fill={fill} fillOpacity={0.12} stroke={fill} strokeWidth="0.6" />
      <text x={width / 2} y={height / 2 + 3} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={fill}>
        {text}
      </text>
    </g>
  );
}

function SourceNote() {
  const t = STRINGS[useDocLocale()];
  return (
    <p className="text-[11px] leading-5 text-muted-foreground">
      {t.sourcePrefix}
      <a
        href="https://rpc.cfainstitute.org/blogs/enterprising-investor/2021/peak-diversification-how-many-stocks-best-diversify-an-equity-portfolio"
        target="_blank"
        rel="noopener noreferrer"
      >
        CFA Institute「Peak Diversification: How Many Stocks Best Diversify an Equity Portfolio?」
      </a>
      {t.sourceSuffix}
    </p>
  );
}

function DiversificationCurveIllustration() {
  const t = STRINGS[useDocLocale()];
  const pts = DATA.map((d) => ({ x: toX(d.n), y: toY(d.risk) }));
  const curvePath = buildCurvePath(pts);

  const yTicks = [20, 30, 40, 50];
  const xTicks = [0, 10, 20, 30, 40, 50];

  const focusA = DATA.find((d) => d.n === 1)!;
  const focusB = DATA.find((d) => d.n === 10)!;

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      className="mx-auto w-full max-w-2xl"
      role="img"
      aria-labelledby="diversification-title diversification-desc"
    >
      <title id="diversification-title">{t.ariaTitle}</title>
      <desc id="diversification-desc">
        {t.ariaDesc}
      </desc>

      {/* グリッド */}
      {yTicks.map((v) => (
        <g key={v}>
          <line
            x1={PAD.left}
            y1={toY(v)}
            x2={VW - PAD.right}
            y2={toY(v)}
            stroke="currentColor"
            strokeWidth="0.7"
            strokeOpacity={0.1}
          />
          <text x={PAD.left - 8} y={toY(v) + 3} textAnchor="end" fontSize="9" fill="currentColor">
            {v}%
          </text>
        </g>
      ))}

      {/* 軸 */}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={VH - PAD.bottom} stroke="currentColor" strokeWidth="1.2" />
      <line
        x1={PAD.left}
        y1={VH - PAD.bottom}
        x2={VW - PAD.right}
        y2={VH - PAD.bottom}
        stroke="currentColor"
        strokeWidth="1.2"
      />

      {/* 市場リスク線 */}
      <line
        x1={PAD.left}
        y1={toY(MARKET_RISK)}
        x2={VW - PAD.right}
        y2={toY(MARKET_RISK)}
        stroke={COLOR.rose}
        strokeWidth="1.5"
        strokeDasharray="5 4"
      />
      <text
        x={VW - PAD.right}
        y={toY(MARKET_RISK) - 8}
        textAnchor="end"
        fontSize="8.5"
        fontWeight="700"
        fill={COLOR.rose}
      >
        {t.marketRisk}
      </text>

      {/* 逓減エリア背景 */}
      <path
        d={`M ${toX(10)} ${VH - PAD.bottom} L ${toX(10)} ${PAD.top} L ${VW - PAD.right} ${PAD.top} L ${VW - PAD.right} ${VH - PAD.bottom} Z`}
        fill={COLOR.amber}
        fillOpacity={0.04}
      />

      {/* 曲線下の面積 */}
      <path
        d={`${curvePath} L ${toX(50)} ${VH - PAD.bottom} L ${toX(1)} ${VH - PAD.bottom} Z`}
        fill={COLOR.sky}
        fillOpacity={0.06}
      />

      {/* メイン曲線 */}
      <path d={curvePath} fill="none" stroke={COLOR.sky} strokeWidth="3" strokeLinecap="round" />

      {/* 主要点 */}
      {[focusA, focusB].map((d) => (
        <g key={d.n}>
          <circle
            cx={toX(d.n)}
            cy={toY(d.risk)}
            r={5}
            className="fill-white dark:fill-zinc-900"
            stroke={COLOR.sky}
            strokeWidth="2"
          />
          <circle cx={toX(d.n)} cy={toY(d.risk)} r={2} fill={COLOR.sky} />
        </g>
      ))}

      {/* X軸目盛り */}
      {xTicks.map((v) => (
        <g key={v}>
          <line
            x1={toX(v)}
            y1={VH - PAD.bottom}
            x2={toX(v)}
            y2={VH - PAD.bottom + 4}
            stroke="currentColor"
            strokeWidth="1"
          />
          <text x={toX(v)} y={VH - PAD.bottom + 16} textAnchor="middle" fontSize="9" fill="currentColor">
            {v}
          </text>
        </g>
      ))}

      {/* 軸ラベル */}
      <text x={PAD.left} y={PAD.top - 12} fontSize="10" fontWeight="700" fill="currentColor">
        {t.riskAxis}
      </text>
      <text x={VW - PAD.right} y={VH - 10} textAnchor="end" fontSize="10" fontWeight="700" fill="currentColor">
        {t.holdingsAxis}
      </text>

      {/* 注釈チップ — 重ならない位置に配置 */}
      <Chip x={toX(8)} y={toY(40)} text={t.chipEarly} fill={COLOR.sky} />
      <Chip x={toX(36)} y={toY(25)} text={t.chipLater} fill={COLOR.amber} />

      {/* 1銘柄の注釈 — 曲線右上に配置 */}
      <line
        x1={toX(focusA.n) + 4}
        y1={toY(focusA.risk) - 6}
        x2={toX(focusA.n) + 28}
        y2={toY(focusA.risk) - 20}
        stroke={COLOR.sky}
        strokeWidth="0.8"
      />
      <text x={toX(focusA.n) + 30} y={toY(focusA.risk) - 22} fontSize="9" fontWeight="700" fill={COLOR.sky}>
        {t.oneHolding}
      </text>

      {/* 10銘柄の注釈 — 曲線下に配置（チップと重ならないように右下方向） */}
      <line
        x1={toX(focusB.n)}
        y1={toY(focusB.risk) + 6}
        x2={toX(focusB.n) - 10}
        y2={toY(focusB.risk) + 24}
        stroke={COLOR.sky}
        strokeWidth="0.8"
      />
      <text
        x={toX(focusB.n) - 12}
        y={toY(focusB.risk) + 28}
        textAnchor="end"
        fontSize="9"
        fontWeight="700"
        fill={COLOR.sky}
      >
        {t.aroundTen}
      </text>
    </svg>
  );
}

export function DocDiversificationCurveDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <DiversificationCurveIllustration />
      <SourceNote />
    </IllustrationCard>
  );
}
